import { Icon } from 'hakobio-react-ui';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { CUSTOM_NODES } from '../../../constants/CUSTOM_NODES';
import { configMessages, versioningMessages } from '../../../lang/messages';
import { config } from '../../../services/config';
import Page from './Page';
import SchemaSUAComponent from './SchemaSUAComponent';
import { Browser } from '../../models/Browsers';
import { useAppSelector } from '../../../store';

interface Props {
  assemblyId: string;
  assemblyFunction: any;
  hashPartitionKey: string;
  exportDate: string;
  exportTimestamp: number;
  time?: number;
  pageNumber: number;
  totalPageNumber: number;
  suaLoaded: Function;
  suaUnloaded: Function;
  legend: any;
  entriesAmount: number;
  setDeletedAssemblies?: any;
}

//TODO A voir si on le met dans le dossier utils(général) ou si on créer un dossier utils par dossier (ex: ReportPanel) car il ne sera utilisé que dans ce dossier
export const connectionComponents = [
  'plug',
  'asepticDisconnector',
  'mechanicDisconnector',
  'quickCoupler',
  'triclampConnector',
  'couplerReducer',
  'lConnector',
  'tConnector',
  'yConnector',
  'xConnector',
  'asepticConnector',
  'sipConnector',
  'stopcockManifold',
];

const SchemaSUA = ({
  assemblyId,
  assemblyFunction,
  hashPartitionKey,
  exportDate,
  exportTimestamp,
  time,
  pageNumber,
  totalPageNumber,
  suaLoaded,
  suaUnloaded,
  legend,
  entriesAmount
}: Props) => {
  const [_assemblyGeneral, setAssemblyGeneral] = useState(null);
  const [_assemblyComponents, setAssemblyComponents] = useState(null);
  // const [_imageDisplay, setImageDisplay] = useState(true);
  const [_src, setSrc] = useState(null);
  const _isSuaLoadedRef = useRef(false);
  const [_isArchived, setIsArchived] = useState(false);
  const [_errorCount, setErrorCount] = useState(0);

  const browser = useAppSelector((state) => state.assemblyEditorSlice.browser);
  const isFirefox = browser === Browser.Firefox;

  const amountOfLinesForOnePage = isFirefox ? 40 : 36;

  const intl = useIntl();

  useEffect(() => {
    if (!_isSuaLoadedRef.current) {
      getCurrentAssembly(assemblyId, hashPartitionKey);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (_isSuaLoadedRef.current) {
        suaUnloaded(assemblyId);
      }
    };
  }, []);

  useEffect(() => {
    if (_assemblyGeneral) {
      setSrc(
        config.BlobURL +
          '/single-use-configurator/assemblies/thumbnail/' +
          _assemblyGeneral.id +
          '/' +
          _assemblyGeneral.hashPartitionKey +
          '_SUA_Annotated.png?nocache=' +
          exportTimestamp
      );
    }
  }, [_assemblyGeneral, exportTimestamp]);

  const getCurrentAssembly = async (id: string, hashPartitionKey: string) => {
    setAssemblyGeneral(assemblyFunction.general);
    setAssemblyComponents(assemblyFunction.components);
    if (assemblyFunction.isArchived) {
      setIsArchived(true);
    }

    if (!time || time === 0) {
      suaLoaded(id);
      _isSuaLoadedRef.current = true;
    }
  };

  if (!_assemblyGeneral) {
    return null;
  }

  const onError = () => {
    if (_errorCount === 0) {
      setSrc(
        config.BlobURL +
          '/single-use-configurator/assemblies/thumbnail/' +
          _assemblyGeneral.id +
          '_SUA_Annotated' +
          '.png?nocache=' +
          exportTimestamp
      );
      setErrorCount(_errorCount + 1);
    } else {
      setSrc(null);
    }
  };

  const components = legend;

  const render = () => {
    if (!components || components.length === 0) {
      return null;
    }

    return (
      <Page
        key={`schemaSUA-${_assemblyGeneral.id}-${pageNumber}`}
        isLastpage={totalPageNumber === pageNumber}>
        <div className="border-bottom py-2">
          <div className="f-row f1-between f2-center">
            <div>
              <span>
                {_assemblyGeneral.type === 'SingleUseAssembly'
                  ? intl.formatMessage(configMessages.sua)
                  : intl.formatMessage(configMessages.reference)}
              </span>
              <span className="px-2">&#124;</span>
              <span className="font-regular">{_assemblyGeneral.name}</span>
              <span className="px-2">&#124;</span>
              <span>
                {`${intl.formatMessage(versioningMessages.version)} ${
                  Number(_assemblyGeneral.version) + 1
                }`}
                {_isArchived ? (
                  <span>{` (${intl.formatMessage(versioningMessages.archived)})`}</span>
                ) : null}
              </span>
            </div>
            <div>{exportDate}</div>
          </div>
        </div>
        <div className="f-col f-grow">
          <div className="f-row f-grow gap-2 pt-3">
            <div className="f-full f-center" style={{ maxHeight: '600px' }}>
              <img
                alt={_assemblyGeneral.name}
                style={{
                  objectFit: 'contain',
                  maxHeight: '100%',
                  maxWidth: '100%',
                  height: 'auto',
                  width: 'auto'
                }}
                src={_src}
                onError={onError}
              />
            </div>
            <div
              className="border-left f-grow px-2 f-col gap-2"
              style={{ width: '30%', flexShrink: 0 }}>
              {components &&
                components.map((component, index) => {
                  return (
                    <SchemaSUAComponent
                      key={component.id + index}
                      component={component}
                      index={index}
                    />
                  );
                })}
            </div>
          </div>
        </div>
        <div className="f-row f1-between f2-center mt-3" style={{ fontSize: 13 }}>
          {time > 0 && (
            <div className="dark-orange f-row f2-center gap-1">
              <Icon name="alert" />
              Continued from previous page
            </div>
          )}
          <div className="f-grow"></div>
          <div>{`Page ${pageNumber}/${totalPageNumber}`}</div>
        </div>
      </Page>
    );
  };

  if (!_assemblyComponents) {
    return null;
  }

  return render();
};

export default SchemaSUA;
